import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'reactstrap';
import get from 'lodash-es/get';
import classnames from 'classnames';
import withSmartListHandlers from 'site-modules/shared/components/color-feature-list-smart/color-feature-list-smart';

import './color-list-smart-v2.scss';

const Color = PropTypes.shape({
  name: PropTypes.string,
  properties: PropTypes.shape({
    rgb: PropTypes.string,
    opacity: PropTypes.string,
  }),
  price: PropTypes.shape({
    baseMSRP: PropTypes.number,
  }),
  disabled: PropTypes.bool,
  trackingId: PropTypes.string,
  trackingValue: PropTypes.string,
});

const Colors = PropTypes.arrayOf(Color);

export const ColorsEntities = {
  Colors,
  Color,
};

/**
 * The component represents item of a color by provided rgb value.
 * rgb can be representation of a color (r, g, b) string or can be 'transparent'.
 * The component changes own view by extra styles and selected icon.
 * */
export function ColorListItem({
  name,
  rgb,
  opacity = null,
  onClick,
  className = 'my-0_25 mx-0_5',
  isSelected,
  isDisabled = false,
  trackingId = null,
  trackingValue = null,
  listName = 'color',
}) {
  const isTransparent = rgb === 'transparent';

  return (
    <label htmlFor={`color-item-${name}`} className={className}>
      <div
        className={classnames('color-item-ring rounded-circle mx-auto p-0_25', {
          'color-ring-selected': isSelected,
        })}
      >
        <input
          id={`color-item-${name}`}
          className={classnames(
            'color-item d-flex rounded-circle mx-auto',
            { 'color-item-selected': isSelected },
            { 'color-item-btn': onClick },
            { 'unknown-color': isTransparent },
            { disabled: isDisabled }
          )}
          style={{ backgroundColor: opacity ? `rgba(${rgb},${opacity})` : `rgb(${rgb})` }}
          name={listName}
          title={name}
          type="radio"
          onChange={onClick}
          value={name}
          data-tracking-id={trackingId}
          data-tracking-value={trackingValue}
          disabled={isDisabled}
          checked={isSelected}
        />
      </div>
      <div className={classnames('size-12 color-item-name pos-r', { 'fw-bold': isSelected })}>
        {name} <span className="visually-hidden">{listName}</span>
      </div>
    </label>
  );
}

ColorListItem.propTypes = {
  name: PropTypes.string.isRequired,
  rgb: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  isSelected: PropTypes.bool.isRequired,
  opacity: PropTypes.string,
  className: PropTypes.string,
  isDisabled: PropTypes.bool,
  trackingId: PropTypes.string,
  trackingValue: PropTypes.string,
  listName: PropTypes.string,
};

const ColorList = ({
  listItems,
  selectedItems = {},
  keyPrefix = '',
  itemClassName = '',
  className = '',
  onClick,
  showCheckmark = true,
  ariaDescribedBy = '',
  ariaInvalid = false,
  listName = 'color',
}) => {
  const missingColors = listItems.some(color => get(color, 'properties.rgb') === 'transparent');
  return (
    <Fragment>
      {missingColors && (
        <span className="d-flex align-items-center size-14">
          <i className="icon-notification2 me-0_5" aria-hidden />
          Some color swatches are unavailable
        </span>
      )}
      <Row
        className={classnames('color-list-smart-v2 mt-1 list-unstyled', className)}
        tag="ul"
        {...(ariaInvalid && ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy, 'aria-invalid': true } : {})}
      >
        {listItems.map(({ name, properties, price, disabled, trackingId, trackingValue }) => {
          const rgb = get(properties, 'rgb');
          const opacity = get(properties, 'opacity');
          const baseMSRP = get(price, 'baseMSRP', 0);

          return !rgb ? null : (
            <Col tag="li" xs={4} md={3} className="text-center mb-2" key={`${keyPrefix} ${name} (${rgb})`}>
              <ColorListItem
                rgb={rgb}
                opacity={opacity}
                name={name}
                baseMSRP={baseMSRP}
                onClick={onClick}
                className={itemClassName}
                isSelected={!!selectedItems[name]}
                isDisabled={disabled}
                showCheckmark={showCheckmark}
                trackingId={trackingId}
                trackingValue={trackingValue}
                listName={listName}
              />
            </Col>
          );
        })}
      </Row>
    </Fragment>
  );
};

ColorList.propTypes = {
  itemClassName: PropTypes.string,
  listItems: ColorsEntities.Colors.isRequired,
  keyPrefix: PropTypes.string,
  selectedItems: ColorsEntities.Color,
  className: PropTypes.string,
  onClick: PropTypes.func.isRequired,
  showCheckmark: PropTypes.bool,
  ariaDescribedBy: PropTypes.string,
  ariaInvalid: PropTypes.bool,
  listName: PropTypes.string,
};

export const ColorListSmartV2 = withSmartListHandlers(ColorList, 'color');
